import type { Employee } from '../interfaces/EmployeeInterface';

/** The stage a loaded timesheet actually sits in. Mirrors the console's
 *  `StatusKey` (kept as its own declaration so this module stays free of the
 *  page-level imports — it must be importable from a plain unit test, and
 *  anything that reaches `api/client` cannot be). */
export type TimesheetStatusKey =
  'unsubmitted' | 'managerPending' | 'submitted' | 'supervisorApproved' | 'payrollApproved';

/**
 * The only fields the inference reads. Both approval wire shapes are covered:
 * manager / required-super / approval-group rows nest the stamp inside
 * `approvalEmployee` and exist from the moment the slot is *required*, while
 * supervisor / payroll rows carry `approvalDateTime` at the top level and exist
 * only once someone actually approved.
 */
interface ApprovalLike {
  requiredEmployee?: unknown;
  approvalEmployee?: unknown;
  approvalDateTime?: unknown;
}

/** An approval row counts only when it was really signed. A required-but-unsigned
 *  slot arrives with `approvalEmployee: undefined` (API `getManagerApprovalsAsTimesheetApproval`
 *  / `getSuperApprovalsAsTimesheetApproval` / `getApprovalGroupsForTimesheet`), which is
 *  exactly the row the old presence-only test misread as an approval. */
const isSatisfied = (entry: ApprovalLike | null | undefined): boolean =>
  !!entry && (entry.approvalEmployee != null || entry.approvalDateTime != null);

/** The approval collections the inference needs off a loaded timesheet. */
export type ApprovalBearingTimesheet = Pick<
  Employee,
  'managerApprovals' | 'superApprovals' | 'payrollApprovals' | 'approvalGroups' | 'supervisorApprovals' | 'requiredSupers'
>;

/**
 * Infer which stage a SUBMITTED timesheet is really in from its approval rows.
 *
 * Derived from approval *satisfaction*, never from array presence: on tenants with
 * `allowManagerApproval` (NPM) `managerApprovals` / `approvalGroups` / `superApprovals`
 * are populated with the required-approver roster at submit time, so "the array is
 * non-empty" says nothing about whether anyone signed. The old test read those rosters
 * as approvals and rendered every pending NPM week as Payroll Pending, which also sent
 * Approve Week down the payroll hop.
 *
 * Order matters — a later stage implies every earlier slot is satisfied (design §3.4):
 *   1. payroll approved  → a payroll approval row exists,
 *   2. supervisor/2nd-level approved → a super approval was actually signed
 *      (`superApprovals` on required-shape tenants, `supervisorApprovals` catches a
 *      signature written without a RequiredSuperApprovals link),
 *   3. 1st-level pending → some required manager or approval-group slot is unsigned,
 *   4. otherwise submitted (2nd Approval Pending).
 *
 * WCDP / 2020PH bare shapes are unchanged by construction: with no required rows the
 * API returns only *actual* approvals — every row it emits is satisfied — so steps 1–2
 * fire exactly where the old length-based test fired, and step 3 is skipped for them
 * both because they have no first-level rows and because `managerApprovalEnabled` is
 * false. `managerApprovalEnabled` defaults to true so the pure function is testable on
 * its own; production passes `config.allowManagerApproval`.
 */
export const inferSubmittedStatusKey = (
  employee: ApprovalBearingTimesheet,
  managerApprovalEnabled: boolean = true,
): TimesheetStatusKey => {
  const payrollRows: ApprovalLike[] = employee.payrollApprovals ?? [];
  if (payrollRows.some((r) => isSatisfied(r))) return 'payrollApproved';

  // TAS-679 (WCDP Modified Duty): supervisor + HR are both required, so one signed slot
  // is not Payroll Pending yet. `requiredSupers` is only sent on a 2+ slot timesheet, so
  // every other tenant and timesheet skips this.
  const mdSlots = employee.requiredSupers ?? [];
  if (mdSlots.length >= 2 && mdSlots.some((s) => !s.approved)) return 'submitted';

  const superRows: ApprovalLike[] = employee.superApprovals ?? [];
  const legacySuperRows: ApprovalLike[] = employee.supervisorApprovals ?? [];
  if (superRows.some((r) => isSatisfied(r)) || legacySuperRows.some((r) => isSatisfied(r))) {
    return 'supervisorApproved';
  }

  if (managerApprovalEnabled) {
    const firstLevelRows: ApprovalLike[] = [
      ...(employee.managerApprovals ?? []),
      ...(employee.approvalGroups ?? []),
    ];
    if (firstLevelRows.some((r) => !isSatisfied(r))) return 'managerPending';
  }

  return 'submitted';
};
